import OpeningHours from "opening_hours";

type OpeningState = "open" | "closed" | "unknown" | "invalid" | "unavailable";

export type OpeningHoursStatus = {
  state: OpeningState;
  label: string;
  nextChange: Date | null;
};

export type OpeningHoursLocation = {
  coordinates?: [number, number];
  countryCode?: string;
  state?: string;
};

export type OpeningHoursDayRow = {
  dayLabel: string;
  isReferenceDay: boolean;
  ranges: string[];
};

export type OpeningHoursWeekTable = {
  rows: OpeningHoursDayRow[];
  ignoredPH: boolean;
};

const DEFAULT_STATUS: OpeningHoursStatus = {
  state: "unavailable",
  label: "Unknown (no opening hours provided)",
  nextChange: null,
};

function isPublicHolidayDataError(error: unknown): boolean {
  const message = String(error ?? "");
  return /no holidays\s*\(PH\)\s*defined/i.test(message) || /country code missing.*holidays/i.test(message);
}

function stripPublicHolidaySelectors(value: string): string {
  const rules = value
    .split(";")
    .map(rule => rule.trim())
    .filter(Boolean)
    .map(rule => {
      const withoutPH = rule
        .replace(/\bPH\b/g, "")
        .replace(/\s*,\s*/g, ",")
        .replace(/,(?=(\d{1,2}:\d{2}|sunrise|sunset|dawn|dusk|open|off|closed)\b)/gi, " ")
        .replace(/^,\s*|\s*,$/g, "")
        .replace(/\s{2,}/g, " ")
        .trim();

      // If PH was the only selector for this rule, dropping it should disable the rule.
      if (/^(off|closed|open)$/i.test(withoutPH)) {
        return "";
      }
      if (/^\d{1,2}:\d{2}([-\+]\d{1,2}:\d{2})?/.test(withoutPH)) {
        return "";
      }
      return withoutPH;
    })
    .filter(Boolean);

  return rules.join("; ");
}

function getNominatimObject(location?: OpeningHoursLocation): {
  lat: number;
  lon: number;
  address: {
    country_code: string;
    state: string;
  };
} | undefined {
  const coordinates = location?.coordinates;
  const countryCode = location?.countryCode?.trim().toLowerCase();
  const state = location?.state?.trim();

  if (!coordinates || !countryCode || !/^[a-z]{2}$/.test(countryCode)) {
    return undefined;
  }

  return {
    lat: coordinates[1],
    lon: coordinates[0],
    address: {
      country_code: countryCode,
      state: state || "unknown",
    },
  };
}

function createOpeningHoursWithFallback(
  openingHoursValue: string,
  location?: OpeningHoursLocation,
): {
  openingHours: OpeningHours;
  ignoredPH: boolean;
  fallbackValue?: string;
} {
  const nominatimObject = getNominatimObject(location);

  try {
    return {
      openingHours: nominatimObject
        ? new OpeningHours(openingHoursValue, nominatimObject)
        : new OpeningHours(openingHoursValue),
      ignoredPH: false,
    };
  } catch (error) {
    if (!isPublicHolidayDataError(error)) {
      throw error;
    }
    const fallbackValue = stripPublicHolidaySelectors(openingHoursValue);
    if (!fallbackValue) {
      throw error;
    }
    return {
      openingHours: new OpeningHours(fallbackValue),
      ignoredPH: true,
      fallbackValue,
    };
  }
}

function evaluateWithPublicHolidayFallback<T>(
  openingHoursValue: string,
  location: OpeningHoursLocation | undefined,
  evaluator: (openingHours: OpeningHours) => T,
): {
  result: T;
  ignoredPH: boolean;
} {
  const initial = createOpeningHoursWithFallback(openingHoursValue, location);
  try {
    return {
      result: evaluator(initial.openingHours),
      ignoredPH: initial.ignoredPH,
    };
  } catch (error) {
    if (!isPublicHolidayDataError(error)) {
      throw error;
    }
    const fallbackValue = stripPublicHolidaySelectors(openingHoursValue);
    if (!fallbackValue) {
      throw error;
    }
    return {
      result: evaluator(new OpeningHours(fallbackValue)),
      ignoredPH: true,
    };
  }
}

function startOfMonday(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const mondayIndex = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - mondayIndex);
  return start;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatHourMinute(date: Date): string {
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function formatDailyRange(
  from: Date,
  to: Date,
  dayStart: Date,
  dayEnd: Date,
  unknown: boolean,
): string {
  const isFullDay = from.getTime() === dayStart.getTime() && to.getTime() === dayEnd.getTime() && !unknown;
  if (isFullDay) {
    return "Open 24 hours";
  }

  const suffix = unknown ? " (uncertain)" : "";
  return `${formatHourMinute(from)} - ${formatHourMinute(to)}${suffix}`;
}

function getPublicHolidayRowRanges(openingHoursValue: string): string[] | null {
  const hasPublicHolidayRule = /\bPH\b/i.test(openingHoursValue);
  if (!hasPublicHolidayRule) {
    return null;
  }

  const ranges = openingHoursValue
    .split(";")
    .map(rule => rule.trim())
    .filter(rule => /\bPH\b/i.test(rule))
    .map(rule => {
      const withoutPH = rule
        .replace(/\bPH\b/gi, "")
        .replace(/\s*,\s*/g, ",")
        .replace(/,(?=(\d{1,2}:\d{2}|sunrise|sunset|dawn|dusk|open|off|closed)\b)/gi, " ")
        .replace(/^,\s*|\s*,$/g, "")
        .replace(/\s{2,}/g, " ")
        .trim();

      const scheduleMatch = withoutPH.match(
        /(24\/7|\d{1,2}:\d{2}\s*[-+]\s*\d{1,2}:\d{2}|sunrise|sunset|dawn|dusk|open|off|closed).*$/i,
      );
      const schedule = (scheduleMatch?.[0] ?? withoutPH).trim();
      if (!schedule) {
        return "Special schedule";
      }

      return schedule
        .replace(/\s*-\s*/g, " - ")
        .replace(/\s{2,}/g, " ")
        .replace(/^open$/i, "Open")
        .replace(/^off$|^closed$/i, "Closed");
    })
    .filter(Boolean);

  return ranges.length > 0 ? ranges : ["Special schedule"];
}

export function getOpeningHoursStatus(
  openingHoursValue: string | undefined,
  at: Date,
  location?: OpeningHoursLocation,
): OpeningHoursStatus {
  if (!openingHoursValue || !openingHoursValue.trim()) {
    return DEFAULT_STATUS;
  }

  try {
    const { result, ignoredPH } = evaluateWithPublicHolidayFallback(openingHoursValue, location, openingHours => {
      const unknown = openingHours.getUnknown(at);
      const open = openingHours.getState(at);
      const nextChange = openingHours.getNextChange(at) ?? null;
      return { unknown, open, nextChange };
    });
    const suffix = ignoredPH ? " (PH ignored)" : "";

    if (result.unknown) {
      return {
        state: "unknown",
        label: `Maybe open at selected time${suffix}`,
        nextChange: result.nextChange,
      };
    }

    if (result.open) {
      return {
        state: "open",
        label: `Open at selected time${suffix}`,
        nextChange: result.nextChange,
      };
    }

    return {
      state: "closed",
      label: `Closed at selected time${suffix}`,
      nextChange: result.nextChange,
    };
  } catch {
    return {
      state: "invalid",
      label: "Unknown (unable to parse opening hours)",
      nextChange: null,
    };
  }
}

export function getOpeningHoursWeekTable(
  openingHoursValue: string | undefined,
  referenceDate: Date,
  location?: OpeningHoursLocation,
): OpeningHoursWeekTable | null {
  if (!openingHoursValue || !openingHoursValue.trim()) {
    return null;
  }

  try {
    const weekStart = startOfMonday(referenceDate);
    const { result: rows, ignoredPH } = evaluateWithPublicHolidayFallback(
      openingHoursValue,
      location,
      openingHours => {
        const dayRows: OpeningHoursDayRow[] = [];
        for (let dayOffset = 0; dayOffset < 7; dayOffset += 1) {
          const dayStart = new Date(weekStart);
          dayStart.setDate(weekStart.getDate() + dayOffset);

          const dayEnd = new Date(dayStart);
          dayEnd.setDate(dayStart.getDate() + 1);

          const intervals = openingHours.getOpenIntervals(dayStart, dayEnd);
          const ranges = intervals.map(([from, to, unknown]) =>
            formatDailyRange(from, to, dayStart, dayEnd, unknown),
          );

          dayRows.push({
            dayLabel: dayStart.toLocaleDateString(undefined, { weekday: "short" }),
            isReferenceDay: isSameDay(dayStart, referenceDate),
            ranges: ranges.length > 0 ? ranges : ["Closed"],
          });
        }

        const publicHolidayRanges = getPublicHolidayRowRanges(openingHoursValue);
        if (publicHolidayRanges) {
          dayRows.push({
            dayLabel: "Public Holidays",
            isReferenceDay: false,
            ranges: publicHolidayRanges,
          });
        }
        return dayRows;
      },
    );

    return { rows, ignoredPH };
  } catch {
    return null;
  }
}
